import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useQuery } from 'react-apollo';
import useStyles from 'isomorphic-style-loader/useStyles';
import s from './News.css';
import { api, contentPageUploadDir } from '../../../../config';
import getNewsContentPages from './getNewsContentPages.graphql';
import useScrollReveal from '../../hooks/useScrollReveal';
import messages from '../../../../locale/messages';
import { hasPlacement } from '../../../../helpers/contentPagePlacement';

function formatDate(dateStr, locale) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(locale || 'id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

function NewsCard({ item, isVisible, delay, locale }) {
  const imageUrl = item.pageBanner
    ? `${api.apiEndpoint}${contentPageUploadDir}medium_${item.pageBanner}`
    : null;

  const dateLabel = formatDate(item.startDate || item.createdAt, locale);
  const excerpt = stripHtml(item.metaDescription || item.content);

  return (
    <a
      href={`/page/${item.pageUrl}`}
      className={`${s.card} ${isVisible ? s.cardVisible : ''}`}
      style={{ transitionDelay: isVisible ? `${delay}s` : '0s' }}
    >
      <div className={s.imageWrap}>
        {imageUrl ? (
          <img src={imageUrl} alt={item.pageTitle} className={s.image} />
        ) : (
          <div className={s.imagePlaceholder}>📰</div>
        )}
      </div>
      <div className={s.body}>
        {dateLabel && <p className={s.meta}>{dateLabel}</p>}
        <h3 className={s.cardTitle}>{item.pageTitle}</h3>
        {excerpt && <p className={s.cardDesc}>{excerpt}</p>}
        <span className={s.readMore}>
          <FormattedMessage {...messages.homeReadMore} /> →
        </span>
      </div>
    </a>
  );
}

function News({ intl }) {
  useStyles(s);
  const { ref, isVisible } = useScrollReveal();
  const { data, loading } = useQuery(getNewsContentPages, { ssr: true });

  const now = new Date();

  const newsItems = (data?.getContentPageDetails || [])
    .filter(item => {
      if (!item.isEnable) return false;
      if (item.placement && !hasPlacement(item.placement, 'WEB')) return false;
      if (item.startDate && new Date(item.startDate) > now) return false;
      if (item.endDate && new Date(item.endDate) < now) return false;
      return true;
    })
    .sort((a, b) => {
      const pa = a.priority ?? 0;
      const pb = b.priority ?? 0;
      if (pa !== pb) return pa - pb;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    })
    .slice(0, 6);

  if (loading || newsItems.length === 0) return null;

  return (
    <section id="news" className={s.news} ref={ref}>
      <div className={s.inner}>
        <div className={`${s.header} ${isVisible ? s.headerVisible : ''}`}>
          <span className={s.badge}>
            <FormattedMessage {...messages.homeNewsBadge} />
          </span>
          <h2 className={s.heading}>
            <FormattedMessage {...messages.homeNewsHeading} />
          </h2>
          <p className={s.sub}>
            <FormattedMessage {...messages.homeNewsSub} />
          </p>
        </div>

        <div className={s.grid}>
          {newsItems.map((item, i) => (
            <NewsCard
              key={item.id}
              item={item}
              isVisible={isVisible}
              delay={0.1 + i * 0.1}
              locale={intl.locale}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default injectIntl(News);
